import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import dig from 'object-dig';
import clsx from 'clsx';

import { Bar, Doughnut } from 'react-chartjs-2';

import { makeStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';

import Table from '~/components/Table/Table';
import tableStyles from '~/components/Table/Table/styles';
import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';
import InputYearPicker from '~/components/Input/InputYearPicker';
import InputDate from '~/components/Input/InputDate';
import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import InputMultiCorporation from '~/components/Input/InputMultiCorporation';

import formatNumber from '~/utils/Number/formatNumber';
import isPresent from '~/utils/isPresent';
import colors from '~/utils/colors';
import Request from '~/providers/Request';

const useStyles = makeStyles((theme) => ({
  cell: {
    color: '#ffffff',
    backgroundColor: theme.palette.purple.light,
  },
  hidden: {
    display: 'none',
  },
}));

function Appointments() {
  const { t } = useTranslation();
  const classesTable = tableStyles();
  const classes = useStyles();

  const dateTypeOptions = {
    created_at: 'De criação',
    schedule_appointment_at: 'Do agendamento',
    doctor_init_at: 'Do início da consulta',
    doctor_end_at: 'Do final da consulta',
  };

  const [dateType, setDateType] = useState('doctor_end_at');
  const [dataInit, setDataInit] = useState(moment().startOf('day').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('day').format());

  const [dataInitYear, setDataInitYear] = useState(
    moment().startOf('year').format()
  );
  const [dataEndYear, setDataEndYear] = useState(
    moment().endOf('year').format()
  );

  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [attendantOptions, setAttendantsOptions] = useState([]);

  const [ignoreDoctors, setIgnoreDoctors] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [corporation, setCorporation] = useState([
    {
      id: null,
      corporation_id: null,
      name: 'Todas',
    },
  ]);

  const [dataAppointments, setDataAppointments] = useState([]);
  const [withAppointments, setWithAppointments] = useState(0);
  const [withoutAppointments, setWithoutAppointments] = useState(0);
  const [showPieAppointment, setShowPieAppointment] = useState(false);
  const [showPieAppointmentBySpecialty, setShowPieAppointmentBySpecialty] =
    useState(false);

  const [dataAppointmentsBySpecialty, setDataAppointmentsBySpecialty] =
    useState([]);

  const [dataAppointmentsByCategory, setDataAppointmentsByCategory] = useState(
    []
  );
  const [showPieAppointmentByCategory, setShowPieAppointmentByCategory] =
    useState(false);

  const [medicalAppointmentsByMonth, setMedicalAppointmentsByMonth] =
    useState(null);

  const [loadingByMonth, setLoadingByMonth] = useState(false);
  const [loadingByYear, setLoadingByYear] = useState(false);

  function createData(description, total) {
    return { description, total };
  }

  const getPieAppointment = () => {
    let labels = [];
    let data = [];
    let pieColors = [];
    setShowPieAppointment(false);

    if (withoutAppointments > 0 || withAppointments > 0) {
      labels = [
        `${t(
          'dashboard.attributes.without_specialty'
        )}: ${withoutAppointments}`,
        `${t('dashboard.attributes.with_specialty')}: ${withAppointments}`,
      ];
      data = [withoutAppointments, withAppointments];
      pieColors = ['#40BDA7', '#336699'];
      setShowPieAppointment(true);
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const getPieAppointmentBySpecialty = () => {
    const labels = [];
    const data = [];
    const pieColors = [];
    setShowPieAppointmentBySpecialty(false);

    if (isPresent(dataAppointmentsBySpecialty)) {
      dataAppointmentsBySpecialty.forEach((element, index) => {
        labels.push(`${element.description}: ${element.total}`);
        data.push(element.total);
        pieColors.push(colors[index]);
      });
      setShowPieAppointmentBySpecialty(true);
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const getPieAppointmentByCategory = () => {
    const labels = [];
    const data = [];
    const pieColors = [];
    setShowPieAppointmentByCategory(false);

    if (isPresent(dataAppointmentsByCategory)) {
      dataAppointmentsByCategory.forEach((element, index) => {
        labels.push(`${element.description}: ${element.total}`);
        data.push(element.total);
        pieColors.push(colors[index]);
      });
      setShowPieAppointmentByCategory(true);
    }

    return {
      labels: labels,
      datasets: [
        {
          backgroundColor: pieColors,
          hoverBackgroundColor: pieColors,
          data: data,
        },
      ],
    };
  };

  const getBarMedicalAppointmentsByMonth = () => {
    return {
      labels: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      datasets: [
        {
          label: t('dashboard.attributes.appointments'),
          borderWidth: 1,
          backgroundColor: '#40BDA7',
          borderColor: '#40BDA7',
          hoverBackgroundColor: '#40BDA7',
          hoverBorderColor: '#40BDA7',
          data: medicalAppointmentsByMonth,
        },
      ],
    };
  };

  const BarLineOptions = {
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            fontSize: 16,
            maxTicksLimit: 12,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            // maxTicksLimit: 12,
            padding: 10,
            fontSize: 16,
          },
          gridLines: {
            color: 'rgb(234, 236, 244)',
            zeroLineColor: 'rgb(234, 236, 244)',
            drawBorder: false,
            // borderDash: [2],
            // zeroLineBorderDash: [2],
          },
        },
      ],
    },
    legend: {
      labels: {
        fontSize: 16,
      },
    },
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      titleMarginBottom: 10,
      titleFontColor: '#6e707e',
      titleFontSize: 18,
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 20,
      yPadding: 20,
      displayColors: false,
      intersect: false,
      mode: 'index',
      caretPadding: 10,
    },
  };

  const pieOptions = {
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 15,
      yPadding: 15,
      displayColors: false,
      caretPadding: 10,
      callbacks: {
        label: function (tooltipItems, data) {
          let i;
          const label = [];
          const l = data.datasets.length;

          const total = data.datasets[0].data.reduce(
            (a, b) => Number(a) + Number(b),
            0
          );
          for (i = 0; i < l; i += 1) {
            const platform = data.labels[tooltipItems.index].split(':')[0];
            const value = data.datasets[0].data[tooltipItems.index];

            let percent = (value / total) * 100;
            percent = formatNumber(percent);

            label[i] = `${platform}: ${percent}%`;
          }
          return label;
        },
      },
    },
    legend: {
      display: true,
      labels: {
        fontSize: 16,
      },
    },
    cutoutPercentage: 60,
  };

  async function fetchDoctors() {
    Request.get('/admin/users_doctors', null, { normalizeJSON: true })
      .then((response) => {
        setDoctorsOptions(response.data);
      })
      .catch((e) => {
        console.log(e);
        setDoctorsOptions([]);
      });
  }

  async function fetchAttendants() {
    Request.get('/admin/users_attendants', null, { normalizeJSON: true }).then(
      (response) => {
        setAttendantsOptions(response.data);
      }
    );
  }

  async function fetchAppointments() {
    setLoadingByMonth(true);

    let doctorsIds = [];
    let ignoreDoctorsIds = [];
    let attendantsIds = [];

    if (isPresent(doctors)) {
      doctorsIds = doctors.map((item) => item.id);
    }

    if (isPresent(ignoreDoctors)) {
      ignoreDoctorsIds = ignoreDoctors.map((item) => item.id);
    }

    if (isPresent(attendants)) {
      attendantsIds = attendants.map((item) => item.id);
    }

    const corpsId = corporation
      ? `[${corporation.map((corp) => corp.id).join(',')}]`
      : null;
    const corpsCorporationId = corporation
      ? `[${corporation.map((corp) => corp.corporation_id).join(',')}]`
      : null;

    const filters = {
      dateType: dateType,
      start: dataInit,
      end: dataEnd,
      doctorsIds,
      ignoreDoctorsIds,
      attendantsIds,
      partners: corpsId,
      corporations: corpsCorporationId,
    };

    Request.get('/admin/dashboard/appointments', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        setLoadingByMonth(false);

        setWithAppointments(dig(response, 'appointmentsWithSpecialty'));
        setWithoutAppointments(dig(response, 'appointmentsWithoutSpecialty'));
        setDataAppointments([
          createData(
            t('dashboard.attributes.without_specialty'),
            dig(response, 'appointmentsWithoutSpecialty')
          ),
          createData(
            t('dashboard.attributes.with_specialty'),
            dig(response, 'appointmentsWithSpecialty')
          ),
          createData(
            t('dashboard.attributes.count_appointments'),
            dig(response, 'appointments')
          ),
          createData(
            t('dashboard.attributes.count_appointments_request_created_at'),
            dig(response, 'appointmentsRequest')
          ),
        ]);

        const appointmentsBySpecialty = [];
        response.appointmentsBySpecialty.forEach((element) => {
          const data = createData(
            dig(element, 'specialty', 'name'),
            dig(element, 'count')
          );
          appointmentsBySpecialty.push(data);
        });
        setDataAppointmentsBySpecialty(appointmentsBySpecialty);

        const appointmentsByCategory = [];
        response.appointmentsByCategory.forEach((element) => {
          const data = createData(
            dig(element, 'category', 'name'),
            dig(element, 'count')
          );
          appointmentsByCategory.push(data);
        });
        setDataAppointmentsByCategory(appointmentsByCategory);

        if (withoutAppointments > 0 || withAppointments > 0) {
          setShowPieAppointment(true);
        }
      })
      .catch((e) => {
        setLoadingByMonth(false);
        console.log(e);
      });
  }

  async function fetchAppointmentsByYear() {
    setLoadingByYear(true);

    const corpsId = corporation
      ? `[${corporation.map((corp) => corp.id).join(',')}]`
      : null;
    const corpsCorporationId = corporation
      ? `[${corporation.map((corp) => corp.corporation_id).join(',')}]`
      : null;

    Request.get(
      '/admin/dashboard/appointments_by_year',
      {
        start: dataInitYear,
        end: dataEndYear,
        partners: corpsId,
        corporations: corpsCorporationId,
      },
      { normalizeJSON: false }
    )
      .then((response) => {
        setLoadingByYear(false);

        const medicalAppointmentsMonth = [];
        for (let i = 1; i <= 12; i++) {
          const appointmentsMonth = response.medicalAppointmentsByMonth.find(
            (data) => data.month === i
          );

          if (appointmentsMonth) {
            medicalAppointmentsMonth.push(appointmentsMonth.count);
          } else {
            medicalAppointmentsMonth.push(0);
          }
        }
        setMedicalAppointmentsByMonth(medicalAppointmentsMonth);
      })
      .catch((e) => {
        setLoadingByYear(false);
        console.log(e);
      });
  }

  useEffect(() => {
    fetchAppointments();
  }, [
    dateType,
    dataInit,
    dataEnd,
    doctors,
    ignoreDoctors,
    attendants,
    corporation,
  ]);

  useEffect(() => {
    fetchAppointmentsByYear();
  }, [dataInitYear, dataEndYear, corporation]);

  useEffect(() => {
    fetchDoctors();
    fetchAttendants();
  }, []);

  return (
    <>
      <Paper
        Icon={LocalHospitalIcon}
        iconColor="blue"
        title={t('dashboard.tabs.appointments')}
        loading={false}
      >
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              name={'dateType'}
              value={dateType}
              label={t('dashboard.attributes.dateType')}
              inputComponent={InputSelect}
              onChange={({ dateType }) => {
                setDateType(dateType);
              }}
              InputProps={{
                options: [
                  {
                    id: 'created_at',
                    name: 'De criação',
                  },
                  { id: 'schedule_appointment_at', name: 'Do agendamento' },
                  {
                    id: 'doctor_init_at',
                    name: 'Do início da consulta',
                  },
                  {
                    id: 'doctor_end_at',
                    name: 'Do final da consulta',
                  },
                ],
                outValueType: 'string',
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('dashboard.attributes.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('dashboard.attributes.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputMultiCorporation
              corporation={corporation}
              setCorporation={setCorporation}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'doctors'}
              label={t('dashboard.attributes.doctor')}
              value={doctors}
              inputComponent={InputSelect}
              onChange={({ doctors }) => {
                setDoctors(doctors);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: doctorsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'doctors'}
              label={t('dashboard.attributes.ignore_doctor')}
              value={ignoreDoctors}
              inputComponent={InputSelect}
              onChange={({ doctors }) => {
                setIgnoreDoctors(doctors);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: doctorsOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputControl
              name={'attendants'}
              label={t('dashboard.attributes.attendant')}
              value={attendants}
              inputComponent={InputSelect}
              onChange={({ attendants }) => {
                setAttendants(attendants);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: attendantOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loadingByMonth} />

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Table
              emptyData={dataAppointments.length === 0}
              loading={loadingByMonth}
              size="small"
              columns={
                <>
                  <TableCell className={classes.cell}>
                    {t('dashboard.attributes.appointments_in_the_period')}:{' '}
                    {dateTypeOptions[dateType]}
                  </TableCell>
                  <TableCell align="right" className={classes.cell}>
                    {t('dashboard.attributes.total')}
                  </TableCell>
                </>
              }
            >
              {dataAppointments.map((row) => {
                return (
                  <TableRow
                    key={row.description}
                    hover
                    className={classesTable.row}
                  >
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </GridItem>
        </GridContainer>

        <div className={clsx(!showPieAppointment && classes.hidden)}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <Doughnut data={getPieAppointment} options={pieOptions} />
            </GridItem>
          </GridContainer>
        </div>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Table
              emptyData={dataAppointmentsBySpecialty.length === 0}
              loading={loadingByMonth}
              size="small"
              columns={
                <>
                  <TableCell className={classes.cell}>
                    {t(
                      'dashboard.attributes.appointments_specialty_in_the_period'
                    )}
                    : {dateTypeOptions[dateType]}
                  </TableCell>
                  <TableCell align="right" className={classes.cell}>
                    {t('dashboard.attributes.total')}
                  </TableCell>
                </>
              }
            >
              {dataAppointmentsBySpecialty.map((row) => {
                return (
                  <TableRow
                    key={row.description}
                    hover
                    className={classesTable.row}
                  >
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </GridItem>
        </GridContainer>

        <div className={clsx(!showPieAppointmentBySpecialty && classes.hidden)}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <Doughnut
                data={getPieAppointmentBySpecialty}
                options={pieOptions}
              />
            </GridItem>
          </GridContainer>
        </div>

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Table
              emptyData={dataAppointmentsByCategory.length === 0}
              loading={loadingByMonth}
              size="small"
              columns={
                <>
                  <TableCell className={classes.cell}>
                    {t(
                      'dashboard.attributes.appointments_category_in_the_period'
                    )}
                    : {dateTypeOptions[dateType]}
                  </TableCell>
                  <TableCell align="right" className={classes.cell}>
                    {t('dashboard.attributes.total')}
                  </TableCell>
                </>
              }
            >
              {dataAppointmentsByCategory.map((row) => {
                return (
                  <TableRow
                    key={row.description}
                    hover
                    className={classesTable.row}
                  >
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </GridItem>
        </GridContainer>

        <div className={clsx(!showPieAppointmentByCategory && classes.hidden)}>
          <GridContainer>
            <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
              <Doughnut
                data={getPieAppointmentByCategory}
                options={pieOptions}
              />
            </GridItem>
          </GridContainer>
        </div>

        <GridContainer justify="center" alignItems="center">
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputYearPicker
              setStartDate={(date) => setDataInitYear(date)}
              setEndDate={(date) => setDataEndYear(date)}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loadingByYear} />

        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <Typography variant="h6">
              {t('dashboard.attributes.count_medical_appointments_by_month')}
            </Typography>
            <span>
              {t('dashboard.attributes.consider_only_for_created_at')}
            </span>
            <Bar
              data={getBarMedicalAppointmentsByMonth}
              options={BarLineOptions}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loadingByYear} />
      </Paper>
    </>
  );
}

export default Appointments;
